<template>
  <transition name="slide-up">
    <div v-if="comparisonCount > 0" 
         class="fixed bottom-0 left-0 right-0 bg-base-100 shadow-lg border-t border-base-200 z-50">
      <div class="max-w-7xl mx-auto px-4 py-3">
        <div class="flex items-center justify-between gap-4">
          <!-- Left side: Properties preview -->
          <div class="flex items-center gap-4 flex-1">
            <div class="flex items-center gap-2">
              <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              <span class="font-semibold text-base-content">
                Comparar propiedades ({{ comparisonCount }}/{{ maxComparison }})
              </span>
            </div>
            
            <!-- Properties thumbnails -->
            <div class="flex gap-2 overflow-x-auto max-w-md">
              <div v-for="property in comparisonProperties" 
                   :key="property.id"
                   class="flex-shrink-0 relative group">
                <div class="w-16 h-16 rounded-lg overflow-hidden bg-base-200">
                  <img v-if="getPropertyImage(property)" 
                       :src="getPropertyImage(property)" 
                       :alt="property.titulo"
                       class="w-full h-full object-cover">
                  <div v-else class="w-full h-full flex items-center justify-center">
                    <svg class="w-6 h-6 text-base-content/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                </div>
                
                <!-- Remove button -->
                <button @click="removeProperty(property.id)"
                        class="absolute -top-2 -right-2 w-6 h-6 bg-error text-error-content rounded-full 
                               flex items-center justify-center opacity-0 group-hover:opacity-100 
                               transition-opacity hover:scale-110">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              
              <!-- Add more slots -->
              <div v-for="n in (maxComparison - comparisonCount)" 
                   :key="`empty-${n}`"
                   class="flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-base-300 
                          flex items-center justify-center">
                <svg class="w-6 h-6 text-base-content/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
              </div>
            </div>
          </div>
          
          <!-- Right side: Actions -->
          <div class="flex items-center gap-3">
            <button @click="clearComparison" 
                    class="btn btn-ghost btn-sm">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Limpiar
            </button>
            
            <button @click="goToComparison" 
                    :disabled="comparisonCount < 2"
                    class="btn btn-primary btn-sm">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              Comparar ({{ comparisonCount }})
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ComparisonBar',
  computed: {
    ...mapGetters('comparison', [
      'comparisonProperties',
      'comparisonCount',
      'maxComparison'
    ]),
  },
  methods: {
    ...mapActions('comparison', [
      'removeFromComparison',
      'clearComparison'
    ]),
    
    getPropertyImage(property) {
      if (property.imagenes && property.imagenes.length > 0) {
        return `${property.imagenes[0].imagen}`;
      }
      return null;
    },
    
    async removeProperty(propertyId) {
      try {
        await this.removeFromComparison(propertyId);
      } catch (error) {
        console.error('Error removing property from comparison:', error);
      }
    },
    
    async clearAll() {
      try {
        await this.clearComparison();
      } catch (error) {
        console.error('Error clearing comparison:', error);
      }
    },
    
    goToComparison() {
      if (this.comparisonCount >= 2) {
        this.$router.push({ name: 'comparison' });
      }
    },
  },
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave {
  transform: translateY(0);
  opacity: 1;
}
</style>
